/**
 * Verplichte velden van een boorbeschrijving, per norm (NEN 5104 / NEN 6693) en werksoort.
 *
 * Bron: de bijlage "Verplichte velden boorbeschrijving — NEN 5104 vs NEN 6693" (okt 2026). Elk
 * veld uit die bijlage staat hier, óók de optionele (O): een beheerder kan op /admin/settings/
 * required-fields een optioneel veld verplicht maken, of een verplicht veld uitzetten. Die
 * afwijkingen staan in `app_settings.required_field_overrides` en gelden voor alle bedrijven.
 *
 * De eindedagcheck (veldapp) en de signalering (backoffice) rekenen altijd met de lijst zoals
 * die nú is: er worden geen uitkomsten bewaard. Zet een beheerder donderdag een veld aan, dan
 * staat het ook bij de dag van dinsdag tussen wat ontbreekt.
 *
 * De `key` van een regel is vast en voor de gebruiker onzichtbaar: daar hangt een afwijking
 * aan. Hernoem hem dus nooit; een regel die vervalt, haal je weg (de afwijking wordt dan
 * genegeerd). Nieuwe regels komen via de code, niet via de pagina.
 */

import type { SoilStandard } from './standards.js';

/** Onderdeel van de boring waar een veld bij hoort. */
export type RequiredFieldPart =
  | 'borehole' | 'layer' | 'soilSample' | 'wellPipe' | 'finish' | 'waterSample' | 'bottle';

export const REQUIRED_FIELD_PARTS: ReadonlyArray<{ id: RequiredFieldPart; label: string }> = [
  { id: 'borehole', label: 'Boring' },
  { id: 'layer', label: 'Laag' },
  { id: 'soilSample', label: 'Grondmonster' },
  { id: 'wellPipe', label: 'Peilbuis' },
  { id: 'finish', label: 'Afwerking' },
  { id: 'waterSample', label: 'Watermonster' },
  { id: 'bottle', label: 'Fles' },
];

/** V = verplicht · V* = verplicht onder voorwaarde · O = optioneel (legenda van de bijlage). */
export type RequiredFieldLevel = 'V' | 'V*' | 'O';

export interface RequiredFieldRule {
  key: string;
  part: RequiredFieldPart;
  /** Vaste veldnaam binnen het onderdeel (Engels, zoals de rest van de code). */
  field: string;
  /** Naam zoals de bijlage hem noemt. */
  label: string;
  standard: SoilStandard;
  /** WERKSOORT-codes waarvoor de regel geldt; leeg = alle werksoorten. */
  workTypes: readonly string[];
  /** Stand volgens de bijlage. */
  level: RequiredFieldLevel;
  /** Standaard verplicht? V en V* wel, O niet. */
  defaultRequired: boolean;
  /** Voorwaarde/toelichting uit de bijlage. */
  condition: string | null;
}

type Cell = RequiredFieldLevel | null; // null = komt in dat regime niet als veld voor ('—')
type Spec = {
  field: string;
  label: string;
  nen5104: Cell;
  nen6693: Cell;
  /** Voorwaarde per norm; een string geldt voor beide. */
  condition?: string | { nen5104?: string; nen6693?: string };
  /** Werksoorten per norm (alleen waar de bijlage ze bij het veld noemt). */
  workTypes?: { nen5104?: string[]; nen6693?: string[] };
};

// Werksoorten zoals de bijlage ze noemt: "VO, waterbodem, NO en asbest".
const VO_WB_NO_ASB = ['verkennend', 'waterbodem', 'nader', 'asbest'];

const SPECS: Record<RequiredFieldPart, Spec[]> = {
  borehole: [
    { field: 'projectCode', label: 'Projectcode', nen5104: 'V', nen6693: 'V' },
    { field: 'fieldworkDate', label: 'Datum uitvoering veldwerk', nen5104: 'V', nen6693: 'V' },
    { field: 'fieldworkCompanies', label: 'Veldwerkbureau(s)', nen5104: 'O', nen6693: 'O' },
    { field: 'projectRemark', label: 'Opmerking project', nen5104: 'O', nen6693: 'O' },
    { field: 'standardReference', label: 'Verwijzing naar de norm', nen5104: null, nen6693: 'V', condition: 'NEN 6693 §6.1' },
    { field: 'researchLocation', label: 'Onderzoekslocatie', nen5104: null, nen6693: 'V', condition: 'NEN 6693 §6.2' },
    { field: 'describer', label: 'Beschrijver en organisatie', nen5104: null, nen6693: 'V', condition: 'NEN 6693 §6.3' },
    { field: 'name', label: 'Boornummer of -code', nen5104: 'V', nen6693: 'V' },
    { field: 'drillingSystem', label: 'Boorsysteem', nen5104: 'V', nen6693: 'V', condition: 'Meerdere boorsystemen per boring mogelijk' },
    { field: 'drillDiameter', label: 'Diameter boorsysteem', nen5104: null, nen6693: 'O' },
    {
      field: 'drillDiameterAsbestos', label: 'Diameter boorsysteem (asbest)', nen5104: null, nen6693: 'V',
      condition: 'Bij asbestonderzoek, minimaal 12 cm', workTypes: { nen6693: ['asbest'] },
    },
    { field: 'drillingDate', label: 'Datum uitvoering boring', nen5104: 'V', nen6693: 'V' },
    { field: 'endDepth', label: 'Einddiepte t.o.v. referentievlak', nen5104: 'V', nen6693: 'V' },
    { field: 'surfaceType', label: 'Maaiveldtype', nen5104: 'O', nen6693: 'O' },
    { field: 'surfaceHeight', label: 'Hoogte maaiveld', nen5104: 'O', nen6693: 'O' },
    { field: 'referencePlane', label: 'Referentievlak maaiveld', nen5104: 'O', nen6693: 'O', condition: 'Alleen als dat niet het maaiveld is' },
    {
      field: 'coordinates', label: 'X- en Y-coördinaat', nen5104: 'O', nen6693: 'V',
      condition: {
        nen5104: 'Alleen uitwisselen als gemeten',
        nen6693: 'Altijd, in RD, WGS84 of ETRS89; niet gemeten in het veld = achteraf bepalen',
      },
    },
    { field: 'groundwaterLevel', label: 'Grondwaterstand', nen5104: 'V*', nen6693: 'V*', condition: 'Indien aangetroffen' },
    { field: 'fieldworkers', label: 'Veldwerker(s)', nen5104: 'V', nen6693: 'V' },
    {
      field: 'passiveOdour', label: 'Passief waargenomen geur (meetpunt)', nen5104: 'V', nen6693: 'V*',
      condition: { nen6693: 'Bij VO, waterbodem, NO en asbest; alleen op meetpuntniveau' },
      workTypes: { nen6693: VO_WB_NO_ASB },
    },
    {
      field: 'asbestosPresence', label: 'Aanwezigheid asbest', nen5104: null, nen6693: 'V*',
      condition: 'Bij VO, waterbodem, NO en asbest; uitwisselen alleen indien van toepassing',
      workTypes: { nen6693: VO_WB_NO_ASB },
    },
    {
      field: 'waterAboveSurface', label: 'Water boven maaiveld of waterbodem', nen5104: null, nen6693: 'V*',
      condition: 'Bij VO, waterbodem en NO (BRL). Bijlage A is milder: alleen bij waterbodem verplicht',
      workTypes: { nen6693: ['verkennend', 'waterbodem', 'nader'] },
    },
    {
      field: 'fieldTools', label: 'Gasdetectie, olie-watertest, andere hulpmiddelen', nen5104: 'V*', nen6693: 'O',
      condition: { nen5104: 'Vastleggen indien gebruikt' },
    },
    { field: 'procedureDeviations', label: 'Afwijkingen van werkvoorschriften', nen5104: 'V*', nen6693: 'V*', condition: 'Altijd vastleggen als je afwijkt, met reden' },
  ],
  layer: [
    { field: 'depth', label: 'Van / tot (laagdiepte)', nen5104: 'V', nen6693: 'V', condition: 'cm of m -mv, op cm nauwkeurig' },
    {
      field: 'layerType', label: 'Laagtype', nen5104: 'V*', nen6693: 'V',
      condition: {
        nen5104: 'Alleen een antropogene laag als zodanig noteren',
        nen6693: 'Altijd (rots, bijzonder, slib, grind, organisch, klei, silt, zand)',
      },
    },
    { field: 'texture', label: 'Grondsoort / textuur (hoofdnaam + bijmengingen)', nen5104: 'V', nen6693: 'V' },
    { field: 'organicMatter', label: 'Organische-stofaandeel', nen5104: null, nen6693: 'V' },
    { field: 'sandMedian', label: 'Zandmediaan', nen5104: null, nen6693: 'V*', condition: 'Bij een zandlaag' },
    { field: 'gravelMedian', label: 'Grindmediaan', nen5104: null, nen6693: 'V*', condition: 'Bij een grindlaag' },
    {
      field: 'mainColour', label: 'Hoofdkleur', nen5104: 'V', nen6693: 'V*',
      condition: { nen6693: 'Bij VO, waterbodem, NO en asbest, standaardmethode §8.3.8' },
      workTypes: { nen6693: VO_WB_NO_ASB },
    },
    { field: 'secondaryColour', label: 'Bijkleur, kleursterkte', nen5104: 'O', nen6693: 'O' },
    { field: 'hydromorphic', label: 'Hydromorfe kenmerken', nen5104: 'V', nen6693: 'V', condition: 'Eis 17 in beide versies; Bijlage A noemt vlekken optioneel' },
    {
      field: 'bzb', label: 'Bijzondere/bodemvreemde bestanddelen: soort + hoeveelheid', nen5104: 'V*', nen6693: 'V*',
      condition: {
        nen5104: 'Indien waargenomen',
        nen6693: 'Indien waargenomen. Totaal aandeel, soorten en hoeveelheidsklasse; lijst Bijlage C',
      },
    },
    { field: 'bzbOrigin', label: 'BZB oorsprong', nen5104: 'O', nen6693: null },
    { field: 'bzbRemark', label: 'BZB opmerking', nen5104: 'O', nen6693: 'O' },
    { field: 'odour', label: 'Geur per laag + intensiteit', nen5104: 'O', nen6693: 'O', condition: { nen6693: 'Bijlage A: geur hoort op meetpuntniveau, niet per laag' } },
    { field: 'pidOilWater', label: 'PID-waarde, olie-watertest', nen5104: 'O', nen6693: 'O' },
    {
      field: 'moistureClass', label: 'Vochtigheidsklasse', nen5104: null, nen6693: 'V*',
      condition: 'Alleen bij asbestonderzoek', workTypes: { nen6693: ['asbest'] },
    },
    { field: 'sludgeConsistency', label: 'Consistentie slib', nen5104: null, nen6693: 'V*', condition: 'Bij slib onder een waterkolom' },
    { field: 'ripening', label: 'Rijping', nen5104: null, nen6693: 'V*', condition: 'Bij slib zonder bovenliggende waterkolom' },
    {
      field: 'organicConsistency', label: 'Consistentie organische laag', nen5104: null, nen6693: 'V*',
      condition: 'BRL: bij NO en asbest. §8.2.5 noemt het minimaal bij elke organische laag',
      workTypes: { nen6693: ['nader', 'asbest'] },
    },
    { field: 'groundwaterColour', label: 'Grondwater: kleur, transparantie', nen5104: null, nen6693: 'V*', condition: 'Indien grondwater aanwezig' },
    { field: 'groundwaterOdour', label: 'Grondwater: geur, BZB', nen5104: null, nen6693: 'O', condition: 'Indien van toepassing' },
    {
      field: 'floatingSinkingLayer', label: 'Drijf- en zaklaagmeting', nen5104: null, nen6693: 'O',
      condition: 'Alleen in snijdende peilbuis (drijflaag) of op scheidende laag (zaklaag)',
    },
  ],
  soilSample: [
    { field: 'name', label: 'Naam/code', nen5104: 'V', nen6693: 'V' },
    { field: 'depth', label: 'Van / tot', nen5104: 'V', nen6693: 'V', condition: 'Van/tot t.o.v. maaiveld' },
    { field: 'date', label: 'Datum', nen5104: 'O', nen6693: 'V' },
    { field: 'samplers', label: 'Veldwerker(s)', nen5104: 'O', nen6693: 'V', condition: 'Naam monsternemer(s)' },
    { field: 'disturbed', label: 'Geroerd / ongeroerd (steekbus)', nen5104: 'V', nen6693: 'V' },
    { field: 'other', label: 'Barcode, verpakking, tijd, PID, opmerking', nen5104: 'O', nen6693: 'O' },
  ],
  wellPipe: [
    { field: 'name', label: 'Naam', nen5104: 'V', nen6693: 'V' },
    { field: 'filterDepth', label: 'Filter van/tot', nen5104: 'V', nen6693: 'V', condition: 'Van/tot t.o.v. maaiveld' },
    { field: 'gravelDepth', label: 'Grind van/tot', nen5104: 'V', nen6693: 'V', condition: 'Van/tot t.o.v. maaiveld' },
    { field: 'bentoniteDepth', label: 'Bentoniet van/tot', nen5104: 'V', nen6693: 'V', condition: 'Van/tot t.o.v. maaiveld' },
    { field: 'workingWater', label: 'Werkwater', nen5104: 'V', nen6693: 'V' },
    { field: 'ecAtInstallation', label: 'EGV bij plaatsing', nen5104: 'V', nen6693: 'V' },
    { field: 'pipeHeight', label: 'Hoogte buis', nen5104: 'V', nen6693: 'V', condition: 'Hoogte t.o.v. maaiveld of referentievlak' },
    { field: 'groundwaterLevel', label: 'Grondwaterstand t.o.v. referentievlak', nen5104: 'V', nen6693: 'V', condition: 'Tijdens monstername' },
    { field: 'groundwaterColour', label: 'Grondwater: kleur, transparantie', nen5104: null, nen6693: 'V*', condition: 'Indien van toepassing' },
    {
      field: 'groundwaterOdour', label: 'Grondwater: geur, BZB, drijf-/zaklaag', nen5104: null, nen6693: 'O',
      condition: 'Drijf-/zaklaag alleen in snijdende peilbuis of op scheidende laag',
    },
    { field: 'other', label: 'Materiaal, doorloop, pH, binnendiameter, gespoeld, filterkous, opmerking', nen5104: 'O', nen6693: 'O' },
  ],
  finish: [
    { field: 'all', label: 'Alle velden', nen5104: 'O', nen6693: 'O' },
  ],
  waterSample: [
    { field: 'name', label: 'Naam/code', nen5104: 'V', nen6693: 'V', condition: 'Protocol 2002' },
    { field: 'date', label: 'Datum', nen5104: 'V', nen6693: 'V' },
    { field: 'samplingDevice', label: 'Monstername-apparaat', nen5104: 'V', nen6693: 'V' },
    { field: 'turbidity', label: 'Troebelheid', nen5104: 'V', nen6693: 'V' },
    { field: 'aerated', label: 'Belucht', nen5104: 'V', nen6693: 'V' },
    { field: 'ec', label: 'EGV', nen5104: 'V', nen6693: 'V' },
    { field: 'oxygen', label: 'Zuurstof', nen5104: 'V', nen6693: 'V' },
    { field: 'fieldworker', label: 'Veldwerker', nen5104: 'V', nen6693: 'V' },
    { field: 'ph', label: 'pH', nen5104: 'V', nen6693: 'O', condition: { nen6693: 'Indien gemeten' } },
    {
      field: 'other', label: 'Tijd, GWS, referentievlak, debiet, kleur, geur, temperatuur, redox, drijf-/zaklaag, voorpompen, opmerking',
      nen5104: 'O', nen6693: 'O',
    },
  ],
  bottle: [
    { field: 'barcode', label: 'Barcode', nen5104: 'V', nen6693: 'V' },
    { field: 'filtered', label: 'Gefilterd', nen5104: 'V', nen6693: 'V' },
  ],
};

// De bijlage kent alleen NEN 5104 en NEN 6693. ISO 14688 bouwt op NEN 6693 voort en volgt
// daarom de NEN 6693-kolom; wel met eigen sleutels (iso14688:…), zodat een bedrijf ze los kan
// aanpassen.
const STANDARDS: SoilStandard[] = ['nen5104', 'nen6693', 'iso14688'];
const COLUMN: Record<SoilStandard, 'nen5104' | 'nen6693'> = { nen5104: 'nen5104', nen6693: 'nen6693', iso14688: 'nen6693' };

/** Vaste sleutel van een regel, bv. `nen6693:layer.sandMedian`. */
export const requiredFieldKey = (standard: SoilStandard, part: RequiredFieldPart, field: string) =>
  `${standard}:${part}.${field}`;

/** Alle regels uit de bijlage, op volgorde van norm, onderdeel en veld. */
export const REQUIRED_FIELD_RULES: ReadonlyArray<RequiredFieldRule> = STANDARDS.flatMap((standard) =>
  REQUIRED_FIELD_PARTS.flatMap(({ id: part }) => SPECS[part].flatMap((s): RequiredFieldRule[] => {
    const column = COLUMN[standard];
    const level = s[column];
    if (!level) return [];
    const condition = typeof s.condition === 'string' ? s.condition : s.condition?.[column] ?? null;
    return [{
      key: requiredFieldKey(standard, part, s.field),
      part,
      field: s.field,
      label: s.label,
      standard,
      workTypes: s.workTypes?.[column] ?? [],
      level,
      defaultRequired: level !== 'O',
      condition,
    }];
  })));

const RULE_BY_KEY = new Map(REQUIRED_FIELD_RULES.map((r) => [r.key, r]));

export const isRequiredFieldKey = (key: unknown): key is string =>
  typeof key === 'string' && RULE_BY_KEY.has(key);

/** Afwijking van een beheerder, opgeslagen per regel-sleutel. */
export interface RequiredFieldOverride {
  required: boolean;
  note: string;
  updatedBy: string | null;
  updatedAt: string | null;
}
export type RequiredFieldOverrides = Record<string, RequiredFieldOverride>;

export const REQUIRED_FIELD_NOTE_MAX = 500;

/** Alleen geldige afwijkingen voor bestaande regels; al het andere valt weg. */
export function sanitizeRequiredFieldOverrides(input: unknown): RequiredFieldOverrides {
  const out: RequiredFieldOverrides = {};
  if (!input || typeof input !== 'object' || Array.isArray(input)) return out;
  for (const [key, v] of Object.entries(input as Record<string, any>)) {
    if (!isRequiredFieldKey(key) || !v || typeof v !== 'object' || typeof v.required !== 'boolean') continue;
    out[key] = {
      required: v.required,
      note: typeof v.note === 'string' ? v.note.slice(0, REQUIRED_FIELD_NOTE_MAX) : '',
      updatedBy: typeof v.updatedBy === 'string' ? v.updatedBy : null,
      updatedAt: typeof v.updatedAt === 'string' ? v.updatedAt : null,
    };
  }
  return out;
}

export interface ResolvedRequiredField extends RequiredFieldRule {
  /** Geldende stand: de afwijking van de beheerder, anders de standaard. */
  required: boolean;
  /** Is er een afwijking opgeslagen (ook als die gelijk is aan de standaard)? */
  overridden: boolean;
  note: string | null;
  updatedBy: string | null;
  updatedAt: string | null;
}

/**
 * De lijst zoals hij nu geldt: de regels uit de code met de afwijkingen erop. Een afwijking
 * voor een sleutel die niet (meer) bestaat, wordt overgeslagen.
 */
export function resolveRequiredFields(overrides?: unknown): ResolvedRequiredField[] {
  const clean = sanitizeRequiredFieldOverrides(overrides);
  return REQUIRED_FIELD_RULES.map((rule) => {
    const o = clean[rule.key];
    return {
      ...rule,
      required: o ? o.required : rule.defaultRequired,
      overridden: !!o,
      note: o?.note ?? null,
      updatedBy: o?.updatedBy ?? null,
      updatedAt: o?.updatedAt ?? null,
    };
  });
}

/**
 * De verplichte regels voor één boring: de norm van het project, en de werksoort (zonder
 * werksoort tellen alleen de regels die voor alle werksoorten gelden).
 */
export function requiredFieldsFor(
  list: ReadonlyArray<ResolvedRequiredField>,
  { standard, workType }: { standard: SoilStandard; workType?: string | null },
): ResolvedRequiredField[] {
  return list.filter((r) => r.required && r.standard === standard
    && (!r.workTypes.length || (!!workType && r.workTypes.includes(workType))));
}
